<template>
  <div data-testid="thank-you-view" class="thank-you-view min-h-screen bg-white">
    <!-- Loading / Authorization gate -->
    <div v-if="isLoading" class="flex items-center justify-center min-h-screen">
      <div class="w-8 h-8 border-2 border-gray-900 border-t-transparent rounded-full animate-spin"></div>
    </div>

    <template v-else-if="isAuthorized">
    <!-- Hero Section -->
    <div data-testid="thank-you-content" class="relative bg-stone-50 py-20 md:py-28 overflow-hidden">
      <!-- Decorative Elements -->
      <div class="absolute top-0 left-0 w-32 h-32 border-l border-t border-gray-200 -translate-x-1/2 -translate-y-1/2"></div>
      <div class="absolute bottom-0 right-0 w-48 h-48 border-r border-b border-gray-200 translate-x-1/4 translate-y-1/4"></div>

      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
        <!-- Success Icon with Animation -->
        <div class="inline-flex items-center justify-center mb-8">
          <div class="relative">
            <div class="w-20 h-20 bg-gray-900 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-10 w-10 text-white check-icon"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <!-- Ripple effect rings -->
            <div class="absolute inset-0 border-2 border-gray-900 ripple-ring"></div>
            <div class="absolute inset-0 border-2 border-gray-900 ripple-ring delay-1"></div>
          </div>
        </div>

        <!-- Confirmation Text -->
        <p class="text-xs uppercase tracking-[0.3em] text-gray-500 mb-4">{{ $t('thank_you.subtitle') }}</p>
        <h1 class="text-3xl md:text-4xl lg:text-5xl font-light text-gray-900 mb-6 leading-tight">
          {{ $t('thank_you.title') }},<br>
          <span class="italic font-normal">{{ displayName }}</span>
        </h1>
        <p class="text-gray-600 text-sm md:text-base max-w-lg mx-auto leading-relaxed">
          {{ $t('thank_you.description') }}
        </p>
      </div>
    </div>

    <!-- Order Details Card -->
    <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
      <div data-testid="booking-summary" class="bg-white border border-gray-200 shadow-sm p-8 md:p-10">
        <!-- Order Number -->
        <div class="text-center border-b border-gray-100 pb-8 mb-8">
          <p class="text-xs uppercase tracking-[0.2em] text-gray-500 mb-2">{{ $t('thank_you.order_ref') }}</p>
          <p data-testid="order-number" class="text-2xl md:text-3xl font-light text-gray-900 tracking-wider">#{{ orderId }}</p>
          <p class="text-xs text-gray-400 mt-2">{{ $t('thank_you.save_record') }}</p>
        </div>

        <!-- Booking Summary Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <!-- Date -->
          <div class="space-y-1">
            <span class="text-xs uppercase tracking-wider text-gray-500">{{ $t('thank_you.details.date') }}</span>
            <p class="text-base font-medium text-gray-900">{{ bookingDate }}</p>
          </div>

          <!-- Packages -->
          <div class="space-y-1">
            <span class="text-xs uppercase tracking-wider text-gray-500">{{ $t('thank_you.details.packages') }}</span>
            <p class="text-base font-medium text-gray-900">{{ packageCount }} {{ packageCount > 1 ? $t('thank_you.details.packages_plural') : $t('thank_you.details.package') }}</p>
          </div>

          <!-- Studio arrival time (if applicable) -->
          <div v-if="studioTime" class="space-y-1">
            <span class="text-xs uppercase tracking-wider text-gray-500">{{ $t('thank_you.details.studio_time') }}</span>
            <p class="text-base font-medium text-gray-900">{{ studioTime }}</p>
          </div>

          <!-- Photo session time (if applicable) -->
          <div v-if="photoTime" class="space-y-1">
            <span class="text-xs uppercase tracking-wider text-gray-500">{{ $t('thank_you.details.photo_time') }}</span>
            <p class="text-base font-medium text-gray-900">{{ photoTime }}</p>
          </div>

          <!-- Order Items -->
          <div class="md:col-span-2 space-y-1">
            <span class="text-xs uppercase tracking-wider text-gray-500">{{ $t('thank_you.details.order_items') }}</span>
            <div v-if="orderItems.length > 0" class="mt-3 space-y-3">
              <div
                v-for="item in orderItems"
                :key="item.id"
                class="border border-gray-200 p-4"
              >
                <div class="flex flex-wrap items-start justify-between gap-2">
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-1">
                      <span class="font-medium text-gray-900">{{ getLocalizedPackageName(item) }}</span>
                      <span
                        v-if="item.package_type"
                        :class="[
                          'px-1.5 py-0.5 text-xs rounded',
                          item.package_type === 'premium'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-gray-100 text-gray-700'
                        ]"
                      >
                        {{ item.package_type }}
                      </span>
                    </div>
                    <p v-if="getLocalizedPackageDescription(item)" class="text-sm text-gray-500 mt-1 line-clamp-2">
                      {{ getLocalizedPackageDescription(item) }}
                    </p>
                    <div class="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                      <span v-if="item.person_name">
                        <span class="font-medium">{{ $t('thank_you.item.person') }}:</span> {{ item.person_name }}
                        <span v-if="item.age">({{ item.age }} {{ $t('thank_you.item.years') }})</span>
                      </span>
                      <span v-if="item.package_pax">
                        <span class="font-medium">{{ $t('thank_you.item.pax') }}:</span> {{ item.package_pax }} {{ $t('thank_you.item.people', item.package_pax) }}
                      </span>
                      <span v-if="getItemDurationDisplay(item)">
                        <span class="font-medium">{{ $t('thank_you.item.duration') }}:</span> {{ getItemDurationDisplay(item) }}
                      </span>
                      <span>
                        <span class="font-medium">{{ $t('thank_you.item.gender') }}:</span> {{ getLocalizedGender(item.gender) }}
                      </span>
                      <span>
                        <span class="font-medium">{{ $t('thank_you.item.age_group') }}:</span> {{ getLocalizedAgeGroup(item.age_group) }}
                      </span>
                    </div>
                    <div v-if="item.selected_services && item.selected_services.length > 0" class="flex flex-wrap gap-1 mt-2">
                      <span
                        v-for="service in item.selected_services"
                        :key="service"
                        class="px-2 py-0.5 text-xs bg-stone-50 text-gray-600 border border-gray-100"
                      >
                        {{ getServiceDisplayName(service) }}
                      </span>
                    </div>
                    <div class="mt-2">
                      <span class="font-semibold text-gray-900">฿{{ Math.round(parseFloat(item.price_at_time || 0)).toLocaleString('en-US') }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <p v-else class="text-base font-medium text-gray-900">-</p>
          </div>
        </div>

        <!-- Shuttle / Meeting Point Info -->
        <div v-if="hasShuttle" class="bg-blue-50 border border-blue-200 p-4 mb-8">
          <p class="text-xs uppercase tracking-wider text-blue-600 mb-2">🚐 {{ $t('thank_you.shuttle.title') }}</p>
          <p class="text-sm text-blue-900">{{ $t('thank_you.shuttle.info') }}</p>
        </div>
        <div v-else-if="hasPhotoService" class="bg-stone-50 border border-gray-200 p-4 mb-8">
          <p class="text-xs uppercase tracking-wider text-gray-500 mb-2">📍 {{ $t('thank_you.no_shuttle.title') }}</p>
          <p class="text-sm text-gray-700">{{ $t('thank_you.no_shuttle.info') }}</p>
          <p class="text-xs text-gray-500 mt-1">{{ $t('thank_you.no_shuttle.address') }}</p>
        </div>

        <!-- Temple Fee Note -->
        <div v-if="hasPhotoService" class="bg-amber-50 border border-amber-200 p-4 mb-8">
          <p class="text-xs uppercase tracking-wider text-amber-600 mb-1">⚠️ {{ $t('thank_you.important.title') }}</p>
          <p class="text-sm text-amber-900">{{ $t('thank_you.important.temple_fee') }}</p>
        </div>

        <!-- Payment Summary -->
        <div class="bg-stone-50 p-6 border border-gray-100">
          <div class="flex justify-between items-center mb-3">
            <span class="text-sm text-gray-600">{{ $t('thank_you.payment.total_price') }}</span>
            <span class="text-gray-900">฿{{ formatPrice(totalPrice) }}</span>
          </div>
          <div class="flex justify-between items-center mb-3">
            <span class="text-sm text-gray-600">{{ $t('thank_you.payment.deposit_paid') }}</span>
            <span class="text-gray-900 font-medium">฿{{ formatPrice(depositPaid) }}</span>
          </div>
          <div class="border-t border-gray-200 pt-3 flex justify-between items-center">
            <span class="text-sm text-gray-900 font-medium">{{ $t('thank_you.payment.balance_due') }}</span>
            <span class="text-xl text-gray-900">฿{{ formatPrice(balanceDue) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Location Section -->
    <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
      <div class="border border-gray-200 overflow-hidden">
        <!-- Map Header -->
        <div class="bg-gray-900 px-6 py-4 flex items-center justify-between">
          <div class="flex items-center gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span class="text-white text-sm font-medium tracking-wide">{{ $t('thank_you.location.title') }}</span>
          </div>
          <a
            :href="googleMapsUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="text-white/80 hover:text-white text-xs underline underline-offset-2 transition-colors"
          >
            {{ $t('thank_you.location.open_maps') }}
          </a>
        </div>

        <!-- Map Toggle -->
        <div class="flex border-b border-gray-200">
          <button
            type="button"
            @click="showImageMap = false"
            :class="[
              'flex-1 px-4 py-3 text-sm font-medium tracking-wide transition-colors flex items-center justify-center gap-2',
              !showImageMap
                ? 'bg-gray-900 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            ]"
          >
            <Map class="w-4 h-4" />
            Google Map
          </button>
          <button
            type="button"
            @click="showImageMap = true"
            :class="[
              'flex-1 px-4 py-3 text-sm font-medium tracking-wide transition-colors flex items-center justify-center gap-2',
              showImageMap
                ? 'bg-gray-900 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            ]"
          >
            <Image class="w-4 h-4" />
            {{ $t('thank_you.location.map_image') }}
          </button>
        </div>

        <!-- Map Embed -->
        <div v-show="!showImageMap" class="aspect-video bg-stone-100 relative">
          <iframe
            :src="embedMapUrl"
            width="100%"
            height="100%"
            style="border:0;"
            allowfullscreen=""
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
            class="absolute inset-0"
          ></iframe>
        </div>

        <!-- Studio Map Image -->
        <div v-show="showImageMap" class="bg-stone-100">
          <img
            :src="'https://achiraya-studio-public.s3.ap-southeast-7.amazonaws.com/assets/achiraya-studio-map.jpg'"
            alt="Achiraya Studio Map"
            class="w-full h-auto object-cover"
          />
        </div>

        <!-- Address -->
        <div class="p-6 bg-white">
          <h3 class="text-sm font-medium text-gray-900 mb-2">{{ $t('thank_you.location.studio_name') }}</h3>
          <p class="text-gray-600 text-sm leading-relaxed">
            {{ $t('thank_you.location.address') }}<br>
            {{ $t('thank_you.location.address_line2') }}
          </p>
          <p class="text-xs text-gray-500 mt-3">
            {{ $t('thank_you.location.arrive_early') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Preparation Tips -->
    <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
      <h2 class="text-xs uppercase tracking-[0.2em] text-gray-500 mb-6 text-center">{{ $t('thank_you.tips.title') }}</h2>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="border border-gray-200 p-5 hover:border-gray-400 transition-colors">
          <div class="flex items-start gap-4">
            <div class="w-10 h-10 bg-stone-50 border border-gray-200 flex items-center justify-center flex-shrink-0">
              <span class="text-lg">✨</span>
            </div>
            <div>
              <h3 class="text-sm font-medium text-gray-900 mb-1">{{ $t('thank_you.tips.fresh_faced.title') }}</h3>
              <p class="text-xs text-gray-600 leading-relaxed">
                {{ $t('thank_you.tips.fresh_faced.desc') }}
              </p>
            </div>
          </div>
        </div>

        <div class="border border-gray-200 p-5 hover:border-gray-400 transition-colors">
          <div class="flex items-start gap-4">
              <div class="w-10 h-10 bg-stone-50 border border-gray-200 flex items-center justify-center flex-shrink-0">
              <span class="text-lg">👗</span>
            </div>
            <div>
              <h3 class="text-sm font-medium text-gray-900 mb-1">{{ $t('thank_you.tips.undergarments.title') }}</h3>
              <p class="text-xs text-gray-600 leading-relaxed">
                {{ $t('thank_you.tips.undergarments.desc') }}
              </p>
            </div>
          </div>
        </div>

        <div class="border border-gray-200 p-5 hover:border-gray-400 transition-colors">
          <div class="flex items-start gap-4">
            <div class="w-10 h-10 bg-stone-50 border border-gray-200 flex items-center justify-center flex-shrink-0">
              <span class="text-lg">💵</span>
            </div>
            <div>
              <h3 class="text-sm font-medium text-gray-900 mb-1">{{ $t('thank_you.tips.cash.title') }}</h3>
              <p class="text-xs text-gray-600 leading-relaxed">
                {{ $t('thank_you.tips.cash.desc') }}
              </p>
            </div>
          </div>
        </div>

        <div class="border border-gray-200 p-5 hover:border-gray-400 transition-colors">
          <div class="flex items-start gap-4">
            <div class="w-10 h-10 bg-stone-50 border border-gray-200 flex items-center justify-center flex-shrink-0">
              <span class="text-lg">📱</span>
            </div>
            <div>
              <h3 class="text-sm font-medium text-gray-900 mb-1">{{ $t('thank_you.tips.contact.title') }}</h3>
              <p class="text-xs text-gray-600 leading-relaxed">
                {{ $t('thank_you.tips.contact.desc') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Calendar & Contact Actions -->
    <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 mb-20">
      <div class="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          @click="addToCalendar"
          class="inline-flex items-center justify-center gap-2 px-8 py-3 border border-gray-300 text-gray-700 text-sm font-medium tracking-wide hover:bg-gray-50 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {{ $t('thank_you.actions.add_to_calendar') }}
        </button>
        <a
          href="https://line.me/R/ti/p/@848akyev?ts=11031733&oat_content=url"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center justify-center gap-2 px-8 py-3 bg-gray-900 text-white text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
          </svg>
          {{ $t('thank_you.actions.contact_line') }}
        </a>
      </div>
    </div>

    <!-- Footer Note -->
    <div class="border-t border-gray-100 py-8">
      <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p class="text-xs text-gray-500 mb-2">
          📧 {{ $t('thank_you.email_sent') }}
        </p>
        <p class="text-xs text-gray-400">
          {{ $t('thank_you.footer') }}
        </p>
        <router-link
          to="/"
          class="inline-flex items-center gap-2 mt-6 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          {{ $t('thank_you.return_home') }}
        </router-link>
      </div>
    </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Map, Image } from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth'
import { SERVICE_DESCRIPTIONS, type ServiceCode } from '@/constants/services'

const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()
const authStore = useAuthStore()

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

const isLoading = ref(true)
const isAuthorized = ref(false)
const orderData = ref<any>(null)

onMounted(async () => {
  const orderId = route.query.order_id as string
  const token = route.query.token as string | undefined

  const url = token
    ? `${API_BASE_URL}/orders/${orderId}?token=${encodeURIComponent(token)}`
    : `${API_BASE_URL}/orders/${orderId}`

  try {
    const response = await fetch(url, {
      headers: authStore.token ? { 'Authorization': `Bearer ${authStore.token}` } : {},
      credentials: 'include'
    })

    if (response.ok) {
      const data = await response.json()
      orderData.value = data
      isAuthorized.value = true
    } else {
      router.replace('/')
    }
  } catch {
    // Network error — redirect to safety
    router.replace('/')
  } finally {
    isLoading.value = false
  }
})

// Get order details from route query, falling back to the order fetched from
// the API. The query params are lost whenever the checkout tab navigated away —
// most notably the 3DS card redirect — so the API response is the reliable source.
const orderId = computed(() => route.query.order_id as string || '000000')
const displayName = computed(() => route.query.name as string || 'Guest')
const bookingDateRaw = computed(
  () => (route.query.date as string) || orderData.value?.booking_date || ''
)
const bookingDate = computed(() => {
  const date = bookingDateRaw.value
  if (!date) return 'TBD'
  const { locale } = useI18n()
  const localeMap: Record<string, string> = { th: 'th-TH', zh: 'zh-TW', en: 'en-US' }
  return new Date(date + 'T00:00:00').toLocaleDateString(localeMap[locale.value] || 'en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
})
const studioTime = computed(
  () => (route.query.studio_time as string) || orderData.value?.studio_slot_time || ''
)
const photoTime = computed(
  () => (route.query.photo_time as string) || orderData.value?.photo_slot_time || ''
)
const packageCount = computed(() => orderItems.value.length || parseInt(route.query.packages as string) || 0)
const orderItems = computed(() => orderData.value?.items || [])

function getLocalizedPackageName(item: any): string {
  const currentLocale = locale.value
  if (currentLocale === 'th' && item.package_name_th) return item.package_name_th
  if (currentLocale === 'zh' && item.package_name_zh) return item.package_name_zh
  return item.package_name_en || item.package_name_th || 'Package'
}

function getLocalizedPackageDescription(item: any): string {
  const currentLocale = locale.value
  if (currentLocale === 'th' && item.package_description_th) return item.package_description_th
  if (currentLocale === 'zh' && item.package_description_zh) return item.package_description_zh
  return item.package_description_en || item.package_description_th || ''
}

function getServiceDisplayName(code: string): string {
  return SERVICE_DESCRIPTIONS[code as ServiceCode] || code
}

function getItemDurationDisplay(item: any): string | null {
  const services = item.selected_services || []
  const hasStudioService = services.includes('makeup') || services.includes('dress')
  const hasPhotoService = services.includes('photo')

  if (hasStudioService && hasPhotoService) {
    return t('thank_you.item.duration_both')
  } else if (hasStudioService) {
    return t('thank_you.item.duration_studio')
  } else if (hasPhotoService) {
    return t('thank_you.item.duration_photo')
  }

  return null
}

function getLocalizedGender(gender: string): string {
  if (!gender) return '-'
  if (gender === 'any') return t('packages.filters.all_genders')
  return t(`packages.filters.${gender}`)
}

function getLocalizedAgeGroup(ageGroup: string): string {
  if (!ageGroup) return '-'
  return t(`packages.filters.${ageGroup}`)
}

const allServiceCodes = computed(() => {
  const codes = new Set<string>()
  orderItems.value.forEach((item: any) => {
    (item.selected_services || []).forEach((s: string) => codes.add(s))
  })
  // Fallback to query params if API data not yet available
  if (codes.size === 0) {
    const servicesParam = route.query.services as string
    if (servicesParam) {
      servicesParam.split(/[,;]/).map(s => s.trim()).filter(Boolean).forEach(s => codes.add(s))
    }
  }
  return Array.from(codes)
})

const hasShuttle = computed(() => allServiceCodes.value.includes('shuttle'))
const hasPhotoService = computed(() => allServiceCodes.value.includes('photo'))
const totalPrice = computed(
  () => parseFloat(route.query.total as string) || parseFloat(orderData.value?.total_price) || 0
)
const depositPaid = computed(
  () => parseFloat(route.query.deposit as string) || parseFloat(orderData.value?.total_deposit) || 0
)
const balanceDue = computed(() => totalPrice.value - depositPaid.value)

// Google Maps URLs
const googleMapsUrl = 'https://maps.app.goo.gl/WNDgzD41KRDEYd9M8'
const embedMapUrl = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3875.6771467429494!2d100.49128619999999!3d13.7379861!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30e299cb61c7822f%3A0x2ed061993e50dd5e!2sAchiraya%20studio!5e0!3m2!1sth!2sth!4v1773374789509!5m2!1sth!2sth'

const showImageMap = ref(false)

function formatPrice(price: number): string {
  return Math.round(price).toLocaleString('en-US')
}

function addToCalendar() {
  const title = encodeURIComponent(`Achiraya Studio Photoshoot - Order #${orderId.value}`)
  const location = encodeURIComponent('Achiraya Studio, Wat Arun, Bangkok')
  const details = encodeURIComponent(`Traditional Thai photoshoot experience at Achiraya Studio. Order Reference: #${orderId.value}`)

  // Build calendar URL (Google Calendar)
  const date = bookingDateRaw.value
  if (date) {
    const time = studioTime.value || photoTime.value || '09:00'
    const startTime = new Date(`${date}T${time}:00`)
    const endTime = new Date(startTime.getTime() + 3 * 60 * 60 * 1000) // 3 hours duration

    const formatDate = (d: Date) => d.toISOString().replace(/[-:]/g, '').split('.')[0]

    const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatDate(startTime)}/${formatDate(endTime)}&location=${location}&details=${details}`
    window.open(calendarUrl, '_blank')
  } else {
    alert('Please save your booking details manually.')
  }
}
</script>

<style scoped>
.check-icon {
  animation: checkDraw 0.5s ease-out 0.3s both;
}

@keyframes checkDraw {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.ripple-ring {
  animation: ripple 2s ease-out infinite;
  opacity: 0;
}

.ripple-ring.delay-1 {
  animation-delay: 0.5s;
}

@keyframes ripple {
  0% {
    transform: scale(1);
    opacity: 0.5;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}
</style>
